import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon/Icon";
import { logEvent } from "@/lib/eventLogger";
import type {
  CompareCatalog,
  CompareFormula,
  ComputeCompareStats,
} from "@/utils/types/compare";
import type { FormulationEntry } from "@/utils/types/compound";
import { CompareSelectPopover } from "./CompareSelectPopover";

type CompareControlProps = {
  catalog: CompareCatalog;
  selected: CompareFormula[];
  onToggle: (f: CompareFormula) => void;
  onClear: () => void;
  computeStats: ComputeCompareStats;
  /** Current Flask formulation — forwarded to the popover as the Δ baseline
   *  so each row can show how it compares to the active flask. */
  currentFormulation: FormulationEntry[];
  // Render in "lit" state when ComparePanel is the active centre column —
  // matches the popover-open visual so the user can see they're already in
  // compare mode.
  active?: boolean;
  // Optional controlled mode — when both `open` and `onOpenChange` are
  // supplied, the parent owns popover visibility. Used by FlaskToolbar to
  // unify the open-slot across Compare / History / Import / Export so
  // opening one closes the others.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Forwarded to the popover so the similar/AI sections can show
   *  "Loading…" copy while server suggestions are still in-flight. */
  suggestionsLoading?: boolean;
  /** Forwarded for analytics. */
  flavorId?: string | null;
  formulaId?: number | null;
};

// Compare button + popover. Used in two places:
//   1. FlaskPanel header  — opens the popover; the active flask becomes
//      ComparePanel as soon as one alt is picked.
//   2. ComparePanel header — same control, kept lit so the user can adjust
//      the selection without leaving compare mode.
export function CompareControl({
  catalog,
  selected,
  onToggle,
  onClear,
  computeStats,
  currentFormulation,
  active = false,
  open: controlledOpen,
  onOpenChange,
  suggestionsLoading = false,
  flavorId = null,
  formulaId = null,
}: CompareControlProps) {
  const isControlled =
    controlledOpen !== undefined && onOpenChange !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? !!controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (isControlled) onOpenChange!(next);
    else setInternalOpen(next);
  };

  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Outside-click only meaningful when the control owns its visibility —
  // controlled mode lets the parent manage cross-popover dismissal.
  useEffect(() => {
    if (!open || isControlled) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setInternalOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, isControlled]);

  const lit = open || active;
  const litStyle = lit
    ? {
        background: "var(--brand-green)",
        color: "var(--brand-cream)",
        borderColor: "var(--brand-green)",
        fontWeight: 500,
      }
    : undefined;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        title="Compare formulae"
        className="btn xs"
        style={litStyle}
        onClick={() => {
          if (!open) {
            logEvent("flavor_workbench_flask_tool_clicked", {
              tool: "compare",
              flavor_id: flavorId,
              formula_id: formulaId,
            });
          }
          setOpen(!open);
        }}
      >
        <Icon name="compare" size={11} />
        <span>Compare</span>
        {selected.length > 0 && (
          <span
            className="mono"
            style={{
              fontSize: 9,
              padding: "1px 5px",
              borderRadius: 3,
              background: lit
                ? "rgba(236,235,225,0.22)"
                : "var(--bg-sunk)",
              color: lit ? "var(--brand-cream)" : "var(--ink-2)",
              border: lit
                ? "1px solid rgba(236,235,225,0.35)"
                : "1px solid var(--line)",
              fontWeight: 600,
              letterSpacing: 0,
            }}
          >
            {selected.length}
          </span>
        )}
        <span style={{ fontSize: 8, opacity: 0.7, marginLeft: 1 }}>▾</span>
      </button>
      {open && (
        <CompareSelectPopover
          catalog={catalog}
          selected={selected}
          onToggle={(f) => {
            // selected.some BEFORE the toggle tells us if this click is
            // selecting or deselecting; flip it for the analytics payload.
            const wasSelected = selected.some((s) => s.id === f.id);
            logEvent("flavor_workbench_compare_toggled", {
              flavor_id: flavorId,
              formula_id: formulaId,
              compared_formula_id: String(f.id),
              compared_formula_name: f.name,
              selected: !wasSelected,
            });
            onToggle(f);
          }}
          onClear={() => {
            onClear();
            setOpen(false);
          }}
          computeStats={computeStats}
          currentFormulation={currentFormulation}
          suggestionsLoading={suggestionsLoading}
        />
      )}
    </div>
  );
}
